import Api from '../axios/api';
import { CompanyResponse } from './company.services';
import { ProductInterface } from '../interfaces/product.interface';
import { ProfileInterface } from '../interfaces/Users.interface';
import { QueryParams } from './orders.services';
import {
	NotificationInterface,
	OrderInterface,
} from '../components/OrdersTable/interfaces/Order.interface';

export type ISODateTimeString = string;
export type UUID = string;
export type UserId = number;
export type ProductId = number;
export type ContractId = number;
export type ContractStatus = string;

export interface ContractLineCreate {
	productId: ProductId;
	productName: string;
	pricePerTonSeller: number;
	pricePerTonBuyer: number;
	contractQtyTons: number;
	markup: number;
	allowedOverdraftPercent?: number;
	ignoreCreditLimit: boolean;
}

export interface CreateContractRequest {
	buyerId: UserId;
	sellerId: UserId;
	expiresAt: ISODateTimeString;
	startsAt: ISODateTimeString;
	lines: ContractLineCreate[];
	buyerRepresentativeUid: UUID;
	sellerRepresentativeUid: UUID;
	idempotencyKey?: string;
}

export interface ContractLine {
	id: ContractId;
	contractId: ContractId;
	productId: ProductId;
	productName: string;
	contractQtyTons: number;
	deliveredQtyTons: number;
	orderedQtyTons: number;
	markup: number;
	product: ProductInterface;
	store?: any;
	pricePerTonSeller: number;
	pricePerTonBuyer: number;
	allowedOverdraftPercent?: number;
	ignoreCreditLimit: boolean;
}

export interface Contract {
	sellerRepresentative?: ProfileInterface;
	buyerRepresentative?: ProfileInterface;
	id: ContractId;
	buyerId: UserId;
	sellerId: UserId;
	status: ContractStatus;
	expiresAt: ISODateTimeString;
	startsAt: ISODateTimeString;
	createdBy: UserId;
	createdAt: ISODateTimeString;
	updatedAt: ISODateTimeString;
	deletedAt?: ISODateTimeString | null;
	lines: ContractLine[];
	buyer: CompanyResponse;
	seller: CompanyResponse;
	ordersInProgress?: number;
	ordersDelivered?: number;
	ordersCount: number;
	orders: OrderInterface[];
	countryCode?: string;
	notifications?: {
		count: number;
		items: NotificationInterface[];
	};
}

export interface ContractsListResponse {
	items: Contract[];
	count: number;
}

export interface GetContractsQueryParams {
	limit?: number;
	offset?: number;
	buyerId?: UserId;
	sellerId?: UserId;
	statuses?: ContractStatus[];
	expiringInDays?: number;
	search?: string;
	orderBy?: 'expiresAt' | 'pricePerTonBuyer' | 'createdAt';
	orderDirection?: 'ASC' | 'DESC';
	countryCode?: string;
}

export interface EditContractLine {
	lineId: number;
	contractQtyTons?: number;
	markup?: number;
	pricePerTonSeller?: number;
	pricePerTonBuyer?: number;
}

export interface EditContractRequest {
	startsAt?: ISODateTimeString;
	expiresAt?: ISODateTimeString;
	lines?: EditContractLine[];
	countryCode?: string;
	status?: string;
}

type ContractsTarget = 'admin' | 'customer' | 'producer';

export class ContractsService {
	static getContracts(
		query?: GetContractsQueryParams,
		target: ContractsTarget = 'admin',
	): Promise<{ data: ContractsListResponse }> {
		return Api.get(`/v1/${target}/contracts`, {
			params: query,
		});
	}

	static getContractById(
		id: ContractId,
		target: ContractsTarget = 'admin',
	): Promise<{ data: Contract }> {
		return Api.get(`/v1/${target}/contracts/${id}`);
	}

	static createContract(payload: CreateContractRequest): Promise<Contract> {
		return Api.post('/v1/admin/contracts', payload);
	}

	static editContract(
		id: ContractId,
		payload: EditContractRequest,
	): Promise<{ data: Contract }> {
		return Api.patch<Contract>(`/v1/admin/contracts/${id}`, payload);
	}

	static getRecentContracts(
		query?: QueryParams,
	): Promise<{ data: { items: Contract[]; count: number } }> {
		return Api.get('/v1/customer/contracts/recent', {
			params: query,
		});
	}
}
